import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from './axiosConfig';
import UIIcon from './UIIcon';
import './HelpCenter.css';

const TYPE_META = {
  guide: { key: 'help.type.guide', icon: 'bookOpen' },
  faq: { key: 'help.type.faq', icon: 'help' },
  document: { key: 'help.type.document', icon: 'fileText' },
  video: { key: 'help.type.video', icon: 'video' },
  link: { key: 'help.type.link', icon: 'externalLink' },
};

export default function HelpCenter() {
  const { t, i18n } = useTranslation();
  const isZh = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh');
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState('all');
  const [type, setType] = useState('all');
  const [selected, setSelected] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const readableDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(isZh ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const categoryName = (item) => isZh ? (item?.name_zh || item?.name || '') : (item?.name || '');
  const resourceCategoryName = (item) => isZh ? (item?.category_name_zh || item?.category_name || '') : (item?.category_name || '');
  const resourceTitle = (item) => isZh ? (item?.title_zh || item?.title || '') : (item?.title || '');
  const resourceSummary = (item) => isZh ? (item?.summary_zh || item?.summary || '') : (item?.summary || '');
  const resourceBody = (item) => isZh ? (item?.body_zh || item?.body || '') : (item?.body || '');
  const typeLabel = (value) => t(TYPE_META[value]?.key || 'help.type.guide', { defaultValue: value || '' });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('help-center/');
      setResources(Array.isArray(res.data?.resources) ? res.data.resources : []);
      setCategories(Array.isArray(res.data?.categories) ? res.data.categories : []);
    } catch (err) {
      setError(!isZh && err?.response?.data?.detail ? err.response.data.detail : t('help.loadFailedDetail'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setSelected(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selected]);

  const types = useMemo(() => {
    const unique = [...new Set(resources.map((item) => item.resource_type).filter(Boolean))];
    return unique.sort();
  }, [resources]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return resources.filter((item) => {
      if (categoryId !== 'all' && String(item.category_id) !== String(categoryId)) return false;
      if (type !== 'all' && item.resource_type !== type) return false;
      if (!q) return true;
      const haystack = [
        item.title, item.title_zh, item.summary, item.summary_zh, item.body, item.body_zh,
        item.category_name, item.category_name_zh, item.resource_type_label,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [resources, query, categoryId, type]);

  const featured = filtered.filter((item) => item.is_featured);
  const regular = filtered.filter((item) => !item.is_featured);

  const download = async (item) => {
    if (!item?.download_url) return;
    setDownloadingId(item.id);
    try {
      const res = await axios.get(item.download_url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = item.file_name || t('help.fileFallback');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert(!isZh && err?.response?.data?.detail ? err.response.data.detail : t('help.downloadFailed'));
    } finally {
      setDownloadingId(null);
    }
  };

  const openExternal = (item) => {
    if (item?.external_url) window.open(item.external_url, '_blank', 'noopener,noreferrer');
  };

  const ResourceCard = ({ item, featuredCard = false }) => {
    const meta = TYPE_META[item.resource_type] || TYPE_META.guide;
    const body = resourceBody(item);
    const canRead = Boolean(body);
    return (
      <article className={`help-card ${featuredCard ? 'help-card-featured' : ''}`}>
        <div className="help-card-topline">
          <span className={`help-type help-type-${item.resource_type || 'guide'}`}>
            <UIIcon name={meta.icon} size={14} /> {typeLabel(item.resource_type)}
          </span>
          {item.is_featured && <span className="help-featured-badge">{t('help.featured')}</span>}
        </div>
        <div className="help-card-copy">
          <span className="help-category-label">{resourceCategoryName(item)}</span>
          <h3>{resourceTitle(item)}</h3>
          <p>{resourceSummary(item) || t('help.defaultSummary')}</p>
        </div>
        <div className="help-card-meta">
          {item.updated_at && <span>{t('help.updated', { date: readableDate(item.updated_at) })}</span>}
          {item.has_file && <span>{item.file_name}</span>}
        </div>
        <div className="help-card-actions">
          {canRead && (
            <button type="button" className="help-action-primary" onClick={() => setSelected(item)}>
              <UIIcon name="bookOpen" size={16} /> {t('help.read')}
            </button>
          )}
          {item.has_file && (
            <button type="button" className="help-action-secondary" onClick={() => download(item)} disabled={downloadingId === item.id}>
              <UIIcon name="download" size={16} /> {downloadingId === item.id ? t('help.downloading') : t('help.download')}
            </button>
          )}
          {item.external_url && (
            <button type="button" className="help-action-secondary" onClick={() => openExternal(item)}>
              <UIIcon name={item.resource_type === 'video' ? 'video' : 'externalLink'} size={16} />
              {item.resource_type === 'video' ? t('help.watch') : t('help.open')}
            </button>
          )}
          {!canRead && !item.has_file && !item.external_url && (
            <button type="button" className="help-action-secondary" disabled>{t('help.unavailable')}</button>
          )}
        </div>
      </article>
    );
  };

  return (
    <section className="help-center-page">
      <div className="help-hero">
        <div className="help-hero-copy">
          <span className="help-kicker"><UIIcon name="bookOpen" size={15} /> {t('help.resourceCenter')}</span>
          <h2>{t('help.title')}</h2>
          <p>{t('help.subtitle')}</p>
        </div>
        <div className="help-hero-stat">
          <strong>{resources.length}</strong>
          <span>{t('help.published_other', { count: resources.length })}</span>
        </div>
      </div>

      <div className="help-toolbar">
        <label className="help-search">
          <UIIcon name="search" size={18} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('help.searchPlaceholder')} aria-label={t('help.searchAria')} />
          {query && <button type="button" onClick={() => setQuery('')} aria-label={t('help.clearSearch')}>×</button>}
        </label>
        <div className="help-filters">
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} aria-label={t('help.filterCategory')}>
            <option value="all">{t('help.allCategories')}</option>
            {categories.map((category) => <option key={category.id} value={category.id}>{categoryName(category)}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} aria-label={t('help.filterType')}>
            <option value="all">{t('help.allTypes')}</option>
            {types.map((value) => <option key={value} value={value}>{typeLabel(value)}</option>)}
          </select>
          <button className="help-refresh" type="button" onClick={load} disabled={loading} title={t('help.refresh')}><UIIcon name="refresh" size={17} /></button>
        </div>
      </div>

      {!loading && categories.length > 0 && (
        <div className="help-category-chips" aria-label={t('help.categoriesAria')}>
          <button type="button" className={categoryId === 'all' ? 'active' : ''} onClick={() => setCategoryId('all')}>{t('help.all')}</button>
          {categories.map((category) => (
            <button key={category.id} type="button" className={String(categoryId) === String(category.id) ? 'active' : ''} onClick={() => setCategoryId(String(category.id))}>{categoryName(category)}</button>
          ))}
        </div>
      )}

      {loading && <div className="help-grid help-loading-grid">{[1,2,3,4,5,6].map((i) => <div className="help-skeleton" key={i}><span/><span/><span/><span/></div>)}</div>}

      {!loading && error && (
        <div className="help-state help-state-error"><UIIcon name="help" size={28} /><h3>{t('help.loadFailed')}</h3><p>{error}</p><button type="button" onClick={load}>{t('help.retry')}</button></div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="help-state"><UIIcon name="search" size={28} /><h3>{t('help.noMatch')}</h3><p>{t('help.noMatchHint')}</p><button type="button" onClick={() => { setQuery(''); setCategoryId('all'); setType('all'); }}>{t('help.clearFilters')}</button></div>
      )}

      {!loading && !error && featured.length > 0 && (
        <div className="help-section">
          <div className="help-section-heading"><div><span>{t('help.startHere')}</span><h3>{t('help.featuredTraining')}</h3></div><small>{t('help.featuredCount', { count: featured.length })}</small></div>
          <div className="help-grid help-featured-grid">{featured.map((item) => <ResourceCard key={item.id} item={item} featuredCard />)}</div>
        </div>
      )}

      {!loading && !error && regular.length > 0 && (
        <div className="help-section">
          <div className="help-section-heading"><div><span>{t('help.library')}</span><h3>{t('help.allResources')}</h3></div><small>{t('help.results_other', { count: filtered.length })}</small></div>
          <div className="help-grid">{regular.map((item) => <ResourceCard key={item.id} item={item} />)}</div>
        </div>
      )}

      {selected && (
        <div className="help-modal-backdrop" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <article className="help-modal" role="dialog" aria-modal="true" aria-labelledby="help-resource-title">
            <div className="help-modal-header">
              <div>
                <span className="help-category-label">{resourceCategoryName(selected)} · {typeLabel(selected.resource_type)}</span>
                <h2 id="help-resource-title">{resourceTitle(selected)}</h2>
                {resourceSummary(selected) && <p>{resourceSummary(selected)}</p>}
              </div>
              <button type="button" className="help-modal-close" onClick={() => setSelected(null)} aria-label={t('help.close')}>×</button>
            </div>
            <div className="help-modal-body">
              {resourceBody(selected).split(/\r?\n/).map((line, idx) => line.trim() ? <p key={idx}>{line}</p> : <div className="help-body-spacer" key={idx} />)}
            </div>
            <div className="help-modal-footer">
              <span>{selected.updated_at ? t('help.updated', { date: readableDate(selected.updated_at) }) : ''}</span>
              <div>
                {selected.has_file && <button type="button" className="help-action-secondary" onClick={() => download(selected)}><UIIcon name="download" size={16}/> {t('help.downloadFile')}</button>}
                {selected.external_url && <button type="button" className="help-action-primary" onClick={() => openExternal(selected)}><UIIcon name="externalLink" size={16}/> {t('help.openResource')}</button>}
              </div>
            </div>
          </article>
        </div>
      )}
    </section>
  );
}
